import React, {
  forwardRef,
  useEffect,
  useCallback,
  useRef,
  useImperativeHandle,
  useMemo
} from "react";
import {
  TOGGLE_SELECT_TYPE,
  ToggleSelect
} from "../../../../Homepage/components/ToggleSelect";
import PersonalInvoiceForm from "./PersonalInvoiceForm";
import CompanyInvoiceForm from "./CompanyInvoiceForm";
import SelfEmployedInvoiceForm from "./SelfEmployedInvoiceForm";
import useIsDesktop from "../../../../../hooks/useIsDesktop";
import classNames from "classnames";
import useInvoices from "../../../../../../../../hooks/useInvoices";
import Loader from "../../../../../../common/Loader";
import { BUSINESS_ACTIVITY_KIND } from "../../../../../../../../redux/modules/invoices/invoiceFields";

const InvoiceForm = forwardRef(({ translate, locale }, ref) => {
  const {
    savedPrivatePersonData,
    savedOtherBusinessData,
    savedSelfEmployedData,
    businessActivityKind: savedBusinessActivityKind,
    loading,
    updateInvoiceFormData,
    invoiceFormData,
    fetchInvoiceCountries
  } = useInvoices();
  const invoiceFormRef = useRef(null);
  const isDesktop = useIsDesktop();

  const businessActivityKind = useMemo(() => {
    return (
      invoiceFormData.businessActivityKind ??
      savedBusinessActivityKind ??
      BUSINESS_ACTIVITY_KIND.SELF_EMPLOYED
    );
  }, [invoiceFormData, savedBusinessActivityKind]);

  useEffect(() => {
    updateInvoiceFormData({
      businessActivityKind
    });
    fetchInvoiceCountries();
  }, []);

  const handleSubmit = useCallback(async () => {
    const formData = await invoiceFormRef.current.submit();

    if (!formData) {
      return false;
    }

    return {
      businessActivityKind,
      privatePersonData:
        businessActivityKind === BUSINESS_ACTIVITY_KIND.PRIVATE_PERSON
          ? formData
          : null,
      otherBusinessData:
        businessActivityKind === BUSINESS_ACTIVITY_KIND.OTHER_BUSINESS
          ? formData
          : null,
      selfEmployedData:
        businessActivityKind === BUSINESS_ACTIVITY_KIND.SELF_EMPLOYED
          ? formData
          : null
    };
  }, [businessActivityKind]);

  useImperativeHandle(
    ref,
    () => ({
      submit: handleSubmit
    }),
    [handleSubmit]
  );

  if (loading) {
    return (
      <div>
        <Loader small />
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-3 mb-6 align-center flex-wrap">
        <strong className={"text-xs font-medium text-black-7"}>
          {translate("invoice-form_toggle")}
        </strong>
        <ToggleSelect
          options={[
            {
              label: translate("invoice-form_self-employed"),
              value: BUSINESS_ACTIVITY_KIND.SELF_EMPLOYED
            },
            {
              label: translate("invoice-form_other-business"),
              value: BUSINESS_ACTIVITY_KIND.OTHER_BUSINESS
            },
            {
              label: translate("invoice-form_personal"),
              value: BUSINESS_ACTIVITY_KIND.PRIVATE_PERSON
            }
          ]}
          type={TOGGLE_SELECT_TYPE.SMALL}
          value={businessActivityKind}
          onChange={value => {
            updateInvoiceFormData({
              businessActivityKind: value
            });
          }}
        />
      </div>
      <div
        className={classNames("grid gap-4", {
          "grid-cols-3": isDesktop,
          "grid-cols-1": !isDesktop
        })}
      >
        {businessActivityKind === BUSINESS_ACTIVITY_KIND.PRIVATE_PERSON ? (
          <PersonalInvoiceForm
            ref={invoiceFormRef}
            translate={translate}
            initialFormData={savedPrivatePersonData}
          />
        ) : null}
        {businessActivityKind === BUSINESS_ACTIVITY_KIND.OTHER_BUSINESS ? (
          <CompanyInvoiceForm
            ref={invoiceFormRef}
            locale={locale}
            translate={translate}
            initialFormData={savedOtherBusinessData}
          />
        ) : null}
        {businessActivityKind === BUSINESS_ACTIVITY_KIND.SELF_EMPLOYED ? (
          <SelfEmployedInvoiceForm
            ref={invoiceFormRef}
            locale={locale}
            translate={translate}
            initialFormData={savedSelfEmployedData}
          />
        ) : null}
      </div>
    </div>
  );
});

export default InvoiceForm;
